import { CSSProperties, FC } from 'react';

import FlexibleColumnTitle from 'components/Tables/FeatureComparisonTable/components/FlexibleColumnTitle';
import FlexibleTableCell from 'components/Tables/FeatureComparisonTable/components/FlexibleTableCell';
import {
  FlexibleComparisonColumnProps,
  FlexibleComparisonRowProps,
} from 'components/Tables/FeatureComparisonTable/config';

interface FlexibleDesktopTableProps {
  tableHeading?: string;
  columns: (FlexibleComparisonColumnProps & { id: string })[];
  rows: (FlexibleComparisonRowProps & { id: string })[];
}

const FlexibleDesktopTable: FC<FlexibleDesktopTableProps> = ({ tableHeading, columns, rows }) => {
  // Row heading column keeps a fixed share; the data columns split the rest evenly, however many there are
  const gridStyle: CSSProperties = {
    gridTemplateColumns: `minmax(200px, 1.2fr) repeat(${columns.length}, minmax(0, 1fr))`,
  };

  return (
    <div
      role="table"
      aria-label={tableHeading || undefined}
      className="relative rounded-xl border border-primary-300/30 bg-[radial-gradient(ellipse_98.08%_31.01%_at_50.00%_49.88%,_rgba(255,_255,_255,_0.03)_0%,_rgba(255,_255,_255,_0)_100%)] backdrop-blur-xl"
    >
      <div role="rowgroup" className="px-6 pb-1 pt-6">
        <div role="row" className="grid items-center" style={gridStyle}>
          <div role="columnheader" className="pl-4 pr-6">
            {tableHeading && (
              <h2 className="font-spaceGrotesk text-[30px] font-medium leading-[38px] text-white">{tableHeading}</h2>
            )}
          </div>
          {columns.map(column => (
            <div key={column.id} role="columnheader" className="flex items-center px-6">
              <FlexibleColumnTitle title={column.title} showPrismaticLogo={column.showPrismaticLogo} />
            </div>
          ))}
        </div>
      </div>

      {rows.length > 0 && (
        <div role="rowgroup" className="p-6">
          <div className="flex flex-col overflow-hidden rounded-lg border border-primary-300/30">
            {rows.map(row => (
              <div
                key={row.id}
                role="row"
                className="grid items-stretch border-b border-white/20 bg-white/5 last:border-b-0"
                style={gridStyle}
              >
                <div role="rowheader" className="flex items-center px-6 py-5">
                  <span className="text-xl leading-[30px] text-white">{row.rowHeading}</span>
                </div>
                {columns.map((column, colIdx) => (
                  <div key={column.id} role="cell" className="relative flex items-center px-6 py-5">
                    {column.highlighted && (
                      <div className="pointer-events-none absolute inset-0 bg-gradient-prismatic opacity-30" />
                    )}
                    <div className="relative z-10 w-full">
                      <FlexibleTableCell {...row.cells?.[colIdx]} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlexibleDesktopTable;
